import { type ReactElement, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface NavigationItemRenderProps {
  className: string;
  'data-state': 'active' | 'inactive';
}

export interface NavigationItemProps {
  active?: boolean;
  children: (props: NavigationItemRenderProps) => ReactElement;
}

export function NavigationItem({ active = false, children }: NavigationItemProps) {
  return children({
    className: cn(
      'mx-2 my-px flex min-h-11 min-w-11 items-center gap-2 rounded-[var(--radius-control)] px-2.5 py-1.5 text-[0.8125rem] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]',
      active
        ? 'bg-[var(--primary-light)] font-semibold text-[var(--primary)]'
        : 'text-[var(--foreground-muted)] hover:bg-[var(--background-muted)]',
    ),
    'data-state': active ? 'active' : 'inactive',
  });
}

export interface NavigationGroupProps {
  label: ReactNode;
  icon?: ReactNode;
  open?: boolean;
  children: ReactNode;
  /** Optional semantic hooks for consuming application shells. */
  ariaLabel?: string;
  testId?: string;
  navLevel?: string;
}

export function NavigationGroup({ label, icon, open = false, children, ariaLabel, testId, navLevel }: NavigationGroupProps) {
  return (
    <details data-testid={testId} data-nav-level={navLevel} aria-label={ariaLabel} className="group mt-2" open={open}>
      <summary className="mx-2 flex min-h-11 min-w-11 cursor-pointer list-none items-center gap-2 rounded-[var(--radius-control)] px-2.5 py-1.5 text-[0.8125rem] font-medium text-[var(--foreground)] hover:bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]">
        {icon}
        {label}
        <span className="material-symbols-outlined ml-auto text-[17px] group-open:rotate-90" aria-hidden="true">chevron_right</span>
      </summary>
      <div className="mt-1">{children}</div>
    </details>
  );
}
